'use client';

import getReadingTime from '@/utils/getReadingTime';
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import Block, { BlockTransitionType } from '@/components/Block/Block';
import { EditorBlock } from '../Redactie/Page/PagesEditor';
import CHARACTERS, { Character } from '@/resources/Characters';
import { BLOCK_TYPES } from '../Block/BlockTypes';
import {
    SubjectProgress,
    useSubjectProgress,
} from '@/hooks/useSubjectProgress';
import { PageBlock } from '@/hooks/usePage';

export type TimelineBlock = {
    id?: number;
    type: string;
    data: Record<string, never>;
    order: number;
    editor_only: boolean;
} & EditorBlock;

interface TimelineProps {
    subjectId: number;
    chapterId: number;
    pageId: number;
    title?: string;
    description?: string;
    blocks: Array<TimelineBlock>;
    isEditable?: boolean;
    isEditorPreview?: boolean;
    addNewBlockComponent?: (
        order: number,
        close: () => void,
    ) => React.ReactNode;
    isAnimated?: boolean;
    isScreenReaderAnnouncementsEnabled?: boolean;
    onContentHeightChanged?: () => void;
    onFinished?: () => void;
    onEdited?: (blocks: EditorBlock[]) => void;
}

// Get the last block with saved progress, or undefined if there is none
// So we can skip to the last block with saved progress when the page loads
const getIndexOfLastBlockWithProgress = (
    subjectBlockProgress: SubjectProgress['blocks'],
    blocks: PageBlock[],
) => {
    let lastBlockIndex = 0;
    for (let i = 0; i < blocks.length; i++) {
        const id = blocks[i].id;
        if (id !== undefined && subjectBlockProgress[id]) {
            lastBlockIndex = i;
        }
    }
    return lastBlockIndex;
};

const Timeline = ({
    subjectId,
    chapterId,
    pageId,
    title,
    description,
    blocks,
    isEditable,
    isEditorPreview,
    addNewBlockComponent,
    isAnimated,
    isScreenReaderAnnouncementsEnabled = true,
    onContentHeightChanged,
    onFinished,
    onEdited,
}: TimelineProps) => {
    const timelineRef = useRef<HTMLDivElement>(null);

    const enableAnnouncements =
        isAnimated && isScreenReaderAnnouncementsEnabled;

    const { subjectProgress } = useSubjectProgress(subjectId);

    // Start immediately if no description is provided, else wait for reading time
    const [shouldStart, setShouldStart] = useState(!description);
    // The last block with progress
    const indexOfLastBlockWithProgress = useMemo<number>(
        () =>
            isEditorPreview
                ? 0
                : getIndexOfLastBlockWithProgress(
                      subjectProgress.blocks,
                      blocks,
                  ),
        [blocks],
    );
    // Keep track of visible blocks count
    const [visibleBlocksCount, setVisibleBlocksCount] = useState<number>(
        isAnimated && !isEditable
            ? indexOfLastBlockWithProgress
            : blocks.length,
    );
    // Get the actual blocks and filter out the editor only blocks if needed
    const visibleBlocks = useMemo(() => {
        const filteredBlocks = blocks.filter(
            (block) => block.editor_only == false || isEditable,
        );
        if (isEditable || !isAnimated) return filteredBlocks;
        return [...filteredBlocks].slice(0, visibleBlocksCount);
    }, [
        blocks,
        visibleBlocksCount,
        isEditable,
        isAnimated,
        indexOfLastBlockWithProgress,
    ]);

    // Start timeline animation
    useEffect(() => {
        if (isEditable) return;
        if (visibleBlocksCount === indexOfLastBlockWithProgress) {
            setTimeout(() => {
                // Start timeline if no description is provided
                if (shouldStart && timelineRef.current) {
                    setVisibleBlocksCount(indexOfLastBlockWithProgress + 1);
                }
                // Start timeline after reading time of description
                if (!shouldStart) {
                    setTimeout(() => {
                        if (timelineRef.current) setShouldStart(true);
                    }, getReadingTime(`${description}`) * 0.75);
                }
            }, 100);
        }
        if (timelineRef.current) onContentHeightChanged?.();
        if (!isAnimated && visibleBlocksCount === blocks.length) {
            onFinished?.();
        }
    }, [visibleBlocksCount, shouldStart]);

    // Reset visible blocks count when blocks change, or when timeline is in preview mode
    useEffect(() => {
        setVisibleBlocksCount(isEditable || !isAnimated ? blocks.length : 0);
    }, [blocks, blocks.length, isEditable, isAnimated]);

    // Go to the next block when the current block is finished
    const tryGoToNextBlockFrom = useCallback(
        (block: EditorBlock) => {
            const lastVisibleBlockIndex = visibleBlocks.length - 1;

            // Check if block is last visible block, otherwise return
            if (block.uuid !== visibleBlocks[lastVisibleBlockIndex].uuid)
                return;

            // Add new block to visible blocks
            setVisibleBlocksCount(
                isAnimated ? visibleBlocksCount + 1 : blocks.length,
            );
            onContentHeightChanged?.();

            // Finish timeline if all blocks are visible
            if (visibleBlocksCount === blocks.length) {
                setTimeout(() => {
                    onFinished?.();
                }, 2000);
            }
        },
        [
            visibleBlocks,
            visibleBlocksCount,
            setVisibleBlocksCount,
            onFinished,
            onContentHeightChanged,
        ],
    );

    // Show all blocks until indexOfLastBlockWithProgress if it changes
    useEffect(() => {
        if (isEditable || !isAnimated) return;
        if (indexOfLastBlockWithProgress > visibleBlocksCount) {
            setVisibleBlocksCount(indexOfLastBlockWithProgress);
        }
    }, [indexOfLastBlockWithProgress]);

    // Get the last active chat character from the last block before the current block.
    // Some blocks have data.character, some don't. Pick the last one that does.
    const getLastActiveChatCharacter = useCallback<
        (blockIndex: number) => Character | undefined
    >(
        (blockIndex) => {
            const lastActiveChatCharacterBlock = visibleBlocks
                .slice(0, blockIndex)
                .reverse()
                .find((block) => block.data.character);

            if (!lastActiveChatCharacterBlock) return undefined;
            return CHARACTERS[lastActiveChatCharacterBlock?.data.character];
        },
        [visibleBlocks],
    );

    // Run preload functions for blocks, if they have them
    useEffect(() => {
        // Only run them if the timeline is animated, otherwise the blocks are already visible and no preloading is needed
        if (isAnimated) {
            blocks.forEach((block) => {
                BLOCK_TYPES[block.type]?.preload?.(block);
            });
        }
    }, [isEditable]);

    return (
        <section
            ref={timelineRef}
            aria-live={enableAnnouncements ? 'polite' : 'off'}
            aria-atomic={enableAnnouncements ? 'false' : undefined}
            aria-relevant={enableAnnouncements ? 'additions text' : undefined}
            className="mx-auto w-full max-w-xl"
            aria-label="Chat"
        >
            {!isEditable && (
                <>
                    {title && (
                        <h1 className="text-center text-2xl font-bold text-secondary-text-lg-aaa">
                            {title}
                        </h1>
                    )}
                    {description && (
                        <p className="text-center font-medium text-secondary-text-base-aaa">
                            {description}
                        </p>
                    )}
                </>
            )}

            <div
                className={`flex w-full flex-col items-stretch ${
                    !isEditable && 'gap-4'
                }`}
            >
                {visibleBlocks.map((block, index) => (
                    <div key={block.uuid}>
                        <Block
                            block={block}
                            subjectId={subjectId}
                            chapterId={chapterId}
                            pageId={pageId}
                            id={block.id}
                            type={block.type}
                            data={block.data}
                            order={block.order}
                            uuid={block.uuid}
                            attachments={block.attachments}
                            media={block.media}
                            updatedAt={block.updatedAt}
                            isEditable={isEditable}
                            isEditorPreview={isEditorPreview}
                            addNewBlockComponent={addNewBlockComponent}
                            // Only animate if isAnimated and equal to or after indexOfLastBlockWithProgress
                            isAnimated={
                                isAnimated &&
                                index >= indexOfLastBlockWithProgress
                            }
                            isFirst={index === 0}
                            isFirstNonEditorOnlyBlock={
                                block.id ===
                                blocks.filter(
                                    (block) => block.editor_only == false,
                                )[0]?.id
                            }
                            isLast={index === blocks.length - 1}
                            activeChatCharacter={getLastActiveChatCharacter(
                                index,
                            )}
                            nextBlockTransitionType={
                                BLOCK_TYPES[blocks[visibleBlocksCount]?.type]
                                    ?.transitionType ??
                                BlockTransitionType.INSTANT
                            }
                            onContentHeightChanged={onContentHeightChanged}
                            onFinished={() => {
                                tryGoToNextBlockFrom(block);
                            }}
                            onEdited={(newBlock) => {
                                const originalBlocks = [...blocks];
                                const newBlocks = originalBlocks.map(
                                    (originalBlock) => {
                                        if (
                                            originalBlock.uuid === newBlock.uuid
                                        ) {
                                            return {
                                                ...originalBlock,
                                                data: newBlock.data,
                                                attachments:
                                                    newBlock.attachments,
                                            };
                                        }
                                        return originalBlock;
                                    },
                                );
                                onEdited?.(newBlocks as Array<EditorBlock>);
                            }}
                            onDeleted={() => {
                                const originalBlocks = [...blocks];
                                const newBlocks = originalBlocks.filter(
                                    (originalBlock) =>
                                        originalBlock.uuid !== block.uuid,
                                );
                                onEdited?.(newBlocks as Array<EditorBlock>);
                            }}
                            onMoved={(direction: number) => {
                                const originalBlocks = [...blocks];
                                let newOrder = block.order + direction;
                                const newBlocks = originalBlocks
                                    .map((originalBlock, index) => {
                                        let order = index + 1;
                                        if (originalBlock.uuid === block.uuid) {
                                            order += direction;
                                            newOrder = order;
                                        }
                                        return {
                                            ...originalBlock,
                                            order,
                                        };
                                    })
                                    .map((newBlock) => ({
                                        ...newBlock,
                                        order:
                                            newBlock.order === newOrder &&
                                            newBlock.uuid !== block.uuid
                                                ? newBlock.order - direction
                                                : newBlock.order,
                                    }))
                                    .sort((a, b) => a.order - b.order);
                                onEdited?.(newBlocks as Array<EditorBlock>);
                            }}
                        />
                    </div>
                ))}
            </div>
        </section>
    );
};

export default Timeline;
